import {BaseComponent} from '../base/component.base.js';
import {escapeHtml} from '../../core/escape-html.js';
import {formatDate, formatEuro, moneyInputValue, toLocalIsoDate} from '../../core/billing/billing.core.js';
import {
    INVALID_RENEWAL,
    clientInvoiceProposal,
    invoiceLinesText,
    isEndOfFebruary,
    linesTotalCents,
    parseRenewal,
    validateClientInvoiceForm,
    validateInvoiceRows,
} from '../../core/billing/client-invoice.core.js';
import template from './client-invoice-dialog.template.html';
import dialogStyles from '../base/form-dialog.styles.css';
import styles from './client-invoice-dialog.styles.css';

const FIELDS = Object.freeze({
    renewal: '#renewal-day',
    invoiceNumber: '#invoice-number',
    invoicedOn: '#invoiced-on',
    lines: '.include',
});
const MONEY = Object.freeze(['amount', 'cost']);

/**
 * Dialog that records one invoice for all active items of a client: `open(group)` with a block of
 * `clientInvoiceGroups`. Changing the renewal date recomputes the periods, and the amounts and costs the
 * owner has not typed in; while it is no valid date, copying and recording are disabled. Submitting a valid
 * form emits `billing-client-invoice-submit` with `{slug, request}`; the driving adapter sends it and then
 * calls `close()` or `showError()`. "Copy invoice lines" emits `clipboard-copy {text, label}`.
 */
export class ClientInvoiceDialogComponent extends BaseComponent {
    /** Gives "today"; replaceable so tests do not depend on the clock. @type {() => Date} */
    today = () => new Date();
    #slug = '';
    /** @type {import('../../shell/types/billing.types.js').BillingItemApiResponse[]} */
    #items = [];
    /** @type {import('../../core/billing/client-invoice.core.js').ProposalLine[]} */
    #lines = [];
    /** Per item id, the money the owner typed in since `open`; a new renewal date no longer prefills it. */
    #edited = new Map();
    /** Ids of the items the owner left off the invoice. */
    #excluded = new Set();
    #submitting = false;
    #renewalValid = true;

    connectedCallback() {
        this.loadTemplate(template, dialogStyles + styles);
        this.$('#form').addEventListener('submit', (e) => this.#handleSubmit(e));
        this.$('#cancel').addEventListener('click', () => this.close());
        this.$('#copy').addEventListener('click', () => this.#copy());
        this.$('#dialog').addEventListener('cancel', (e) => {
            if (this.#submitting) e.preventDefault();
        });
        for (const id of ['#renewal-day', '#renewal-month']) {
            this.$(id).addEventListener('input', () => this.#propose());
        }
        this.$('#lines').addEventListener('input', (e) => this.#handleLineInput(e));
    }

    /**
     * @param {{slug: string, name: string, items: object[], renewal: {month: number, day: number}, suggested: boolean}} group
     */
    open(group) {
        this.#slug = group.slug;
        this.#items = group.items;
        this.#edited = new Map();
        this.#excluded = new Set();
        this.$('#dialog-client').textContent = group.name;
        this.$('#renewal-day').value = String(group.renewal.day);
        this.$('#renewal-month').value = String(group.renewal.month);
        this.$('#renewal-suggested').hidden = !group.suggested;
        this.$('#invoice-number').value = '';
        this.$('#invoiced-on').value = toLocalIsoDate(this.today());
        this.#showErrors({});
        this.showError(null);
        this.submitting = false;
        this.#propose();
        this.$('#dialog').showModal();
        this.$('#invoice-number').focus();
    }

    close() {
        this.$('#dialog').close();
    }

    /**
     * @param {string | null} message null hides the error
     * @returns {boolean} whether the dialog is open, so the message is on screen
     */
    showError(message) {
        const error = this.$('#dialog-error');
        error.textContent = message || '';
        error.hidden = !message;
        return this.$('#dialog').open;
    }

    /** @param {boolean} value disables the buttons while a request runs, so it cannot be sent twice */
    set submitting(value) {
        this.#submitting = value;
        this.#updateButtons();
    }

    #updateButtons() {
        this.$('#cancel').disabled = this.#submitting;
        for (const id of ['#submit', '#copy']) this.$(id).disabled = this.#submitting || !this.#renewalValid;
    }

    #renewal() {
        return parseRenewal(this.$('#renewal-month').value, this.$('#renewal-day').value);
    }

    /** Recomputes the lines for a valid renewal date; while it is not one, the lines stay as they are. */
    #propose() {
        const renewal = this.#renewal();
        this.#renewalValid = renewal !== null;
        const error = this.$('#error-renewal');
        error.textContent = this.#renewalValid ? '' : INVALID_RENEWAL;
        error.hidden = this.#renewalValid;
        this.$('#renewal-day').setAttribute('aria-invalid', String(!this.#renewalValid));
        this.#updateButtons();
        this.$('#renewal-end-of-february').hidden = renewal === null || !isEndOfFebruary(renewal);
        if (renewal === null) return;
        const {lines, later} = clientInvoiceProposal(this.#items, renewal, toLocalIsoDate(this.today()));
        this.#lines = lines;
        this.$('#lines').innerHTML = lines.map((line) => this.#row(line)).join('') + later.map((item) => `
            <tr class="later"><td></td><td>${escapeHtml(item.label)}</td>
                <td colspan="3">Paid until ${formatDate(item.paidUntil)}, not on this invoice</td></tr>`).join('');
        this.#updateTotal();
    }

    #row(line) {
        const {item} = line;
        const label = escapeHtml(item.label);
        const edited = this.#edited.get(item.id) ?? {};
        const value = (name) => escapeHtml(edited[name] ?? moneyInputValue(line[name]));
        const bridge = line.bridge
            ? ` <span class="badge badge-gray">Bridge</span> <span class="duration">${line.duration}</span>`
            : '';
        const money = (name, what) => `
                <td class="money">
                    <input class="${name}" data-id="${item.id}" type="text" inputmode="decimal" autocomplete="off"
                        value="${value(name)}" aria-label="${what} of ${label}" aria-describedby="error-${name}-${item.id}" />
                    <p id="error-${name}-${item.id}" class="field-error" hidden></p>
                </td>`;
        return `
            <tr data-id="${item.id}">
                <td><input class="include" data-id="${item.id}" type="checkbox" ${this.#excluded.has(item.id) ? '' : 'checked'}
                    aria-label="Include ${label}" /></td>
                <td>${label}</td>
                <td class="period">${formatDate(item.paidUntil)} to ${formatDate(line.periodTo)}${bridge}</td>
                ${money('amount', 'Amount')}
                ${money('cost', 'Supplier cost')}
            </tr>`;
    }

    #handleLineInput(e) {
        const id = Number(e.target.dataset.id);
        if (e.target.classList.contains('include')) {
            if (e.target.checked) this.#excluded.delete(id);
            else this.#excluded.add(id);
        } else {
            const name = MONEY.find((candidate) => e.target.classList.contains(candidate));
            this.#edited.set(id, {...this.#edited.get(id), [name]: e.target.value});
        }
        this.#updateTotal();
    }

    #rows() {
        return this.#lines.map((line) => ({
            itemId: line.item.id,
            include: !this.#excluded.has(line.item.id),
            periodFrom: line.item.paidUntil,
            periodTo: line.periodTo,
            amount: this.$(`.amount[data-id="${line.item.id}"]`).value,
            cost: this.$(`.cost[data-id="${line.item.id}"]`).value,
        }));
    }

    /** The total of the included amounts, or a dash while one of them is no valid amount. */
    #updateTotal() {
        const {errors, lines} = validateInvoiceRows(this.#rows());
        const valid = Object.keys(errors).every((key) => key === 'lines');
        this.$('#total').textContent = valid ? formatEuro(linesTotalCents(lines) / 100) : '-';
    }

    #copy() {
        const {errors, lines} = validateInvoiceRows(this.#rows());
        this.#showErrors(errors);
        if (Object.keys(errors).length > 0) return;
        const text = invoiceLinesText(lines.map((line) => {
            const {item, bridge} = this.#lines.find((candidate) => candidate.item.id === line.itemId);
            return {label: item.label, periodFrom: item.paidUntil, periodTo: line.periodTo, bridge, amount: line.amount};
        }));
        this.emit('clipboard-copy', {text, label: 'Invoice lines'});
    }

    #showErrors(errors) {
        const show = (error, input, message) => {
            error.textContent = message ?? '';
            error.hidden = !message;
            input?.setAttribute('aria-invalid', String(Boolean(message)));
        };
        for (const [name, selector] of Object.entries(FIELDS)) {
            show(this.$(`#error-${name}`), name === 'lines' ? null : this.$(selector), errors[name]);
        }
        for (const line of this.#lines) {
            for (const name of MONEY) {
                const key = `${name}-${line.item.id}`;
                show(this.$(`#error-${key}`), this.$(`.${name}[data-id="${line.item.id}"]`), errors[key]);
            }
        }
        const order = ['renewal', 'invoiceNumber', 'invoicedOn',
            ...this.#lines.flatMap((line) => MONEY.map((name) => `${name}-${line.item.id}`)), 'lines'];
        const first = order.find((key) => errors[key]);
        if (first) {
            const [name, id] = first.split('-');
            this.$(id ? `.${name}[data-id="${id}"]` : FIELDS[first]).focus();
        }
    }

    #handleSubmit(e) {
        e.preventDefault();
        this.showError(null);
        const {valid, errors, request} = validateClientInvoiceForm({
            invoiceNumber: this.$('#invoice-number').value,
            invoicedOn: this.$('#invoiced-on').value,
            renewal: this.#renewal(),
            rows: this.#rows(),
        });
        this.#showErrors(errors);
        if (valid) {
            this.emit('billing-client-invoice-submit', {slug: this.#slug, request});
        }
    }
}

customElements.define('client-invoice-dialog', ClientInvoiceDialogComponent);
